import { cur } from '@/lib/currency';
import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, ArrowRight, Check, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { localToday } from '@/lib/dateRange';

/**
 * BulkWhatsAppButton — iter403.
 *
 * Sequential WhatsApp queue for sending statements to overdue customers.
 *
 * Why a queue (not a parallel blast)?
 *   - Browsers block more than one popup per click, so we can't open
 *     N WhatsApp tabs at once.
 *   - The user has to attach the downloaded PDF in WhatsApp manually
 *     anyway, so a "next → send → next" flow is the right pace.
 *
 * Flow:
 *   1. Click button → modal lists all customers with credit_balance > minBalance.
 *   2. Optional minBalance threshold (default 1 SR — i.e. anyone owing).
 *   3. "Start" → opens the first customer's PDF + WhatsApp deep link.
 *   4. After sending in WA, user comes back, marks "sent" → next.
 *   5. "Skip" advances without marking sent (e.g. customer has no phone).
 *   6. Progress: "5 of 17 sent · 12 remaining".
 */
const SR = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function BulkWhatsAppButton({ customers = [], balances = [], onShare }) {
  const [open, setOpen] = useState(false);
  const [minBalance, setMinBalance] = useState(1);
  const [running, setRunning] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [sentIds, setSentIds] = useState(new Set());
  const [skippedIds, setSkippedIds] = useState(new Set());

  // Customers with credit_balance >= threshold, sorted by balance desc
  const overdue = useMemo(() => {
    const balMap = Object.fromEntries(balances.map((b) => [b.id, b.credit_balance || 0]));
    return customers
      .map((c) => ({ ...c, credit_balance: balMap[c.id] || 0 }))
      .filter((c) => c.credit_balance >= minBalance)
      .sort((a, b) => b.credit_balance - a.credit_balance);
  }, [customers, balances, minBalance]);

  const totalDue = overdue.reduce((s, c) => s + c.credit_balance, 0);
  const current = running && overdue[currentIdx] ? overdue[currentIdx] : null;
  const sentCount = sentIds.size;
  const skippedCount = skippedIds.size;
  const remaining = overdue.length - sentCount - skippedCount;

  const reset = () => {
    setRunning(false);
    setCurrentIdx(0);
    setSentIds(new Set());
    setSkippedIds(new Set());
  };
  const close = () => { setOpen(false); reset(); };

  // iter404 — Bulk download all queue PDFs as a single ZIP. Saves the
  // user from clicking through each customer's WhatsApp tab when all
  // they want is the PDFs on disk for email or batch upload.
  const [zipping, setZipping] = useState(false);
  const downloadZip = async () => {
    if (overdue.length === 0) return;
    setZipping(true);
    try {
      const ids = overdue.map((c) => c.id);
      const res = await api.post('/customers/statements/bulk-pdf-zip', { customer_ids: ids }, { responseType: 'blob' });
      const generated = res.headers?.['x-statements-generated'];
      const skipped = res.headers?.['x-statements-skipped'];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      const today = localToday().replaceAll('-', '');
      a.download = `customer_statements_${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const skMsg = Number(skipped) > 0 ? ` (${skipped} skipped)` : '';
      toast.success(`Downloaded ${generated || ids.length} statements as ZIP${skMsg}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to bundle statements');
    } finally { setZipping(false); }
  };

  const start = () => {
    if (overdue.length === 0) return;
    setRunning(true);
    setCurrentIdx(0);
    // Auto-trigger the first share so the user goes straight to WhatsApp
    setTimeout(() => onShare(overdue[0]), 80);
  };

  const advance = (markSent) => {
    const cust = overdue[currentIdx];
    if (markSent) setSentIds((prev) => new Set(prev).add(cust.id));
    else setSkippedIds((prev) => new Set(prev).add(cust.id));
    const nextIdx = currentIdx + 1;
    if (nextIdx >= overdue.length) {
      // Done
      setRunning(false);
      setCurrentIdx(0);
      return;
    }
    setCurrentIdx(nextIdx);
    setTimeout(() => onShare(overdue[nextIdx]), 80);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="rounded-full h-9 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
        data-testid="bulk-whatsapp-btn"
      >
        <MessageCircle size={14} className="mr-1.5" />Send to overdue
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else setOpen(true); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle>{running ? `Sending statements (${sentCount + skippedCount + 1} of ${overdue.length})` : 'Send statements to overdue customers'}</DialogTitle>
            <DialogDescription>
              {running
                ? 'Send via WhatsApp on the popup that just opened, then come back and mark this one as Sent or Skip.'
                : 'Each customer\'s PDF will download and a WhatsApp deep-link will open. Attach the PDF in WhatsApp and send. Repeat one-by-one.'}
            </DialogDescription>
          </DialogHeader>

          {!running && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-3">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">Min balance (SR)</Label>
                <Input
                  type="number"
                  min={0}
                  value={minBalance}
                  onChange={(e) => setMinBalance(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="h-8 text-sm"
                  data-testid="bulk-whatsapp-min-balance"
                />
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-3 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Customers in queue</div>
                  <div className="font-bold tabular-nums text-base">{overdue.length}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total receivable</div>
                  <div className="font-bold tabular-nums text-base text-warning">{cur()} {SR(totalDue)}</div>
                </div>
              </div>
              <div className="rounded-lg border border-border max-h-[200px] overflow-y-auto" data-testid="bulk-whatsapp-list">                {overdue.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">No customers above {cur()} {SR(minBalance)}</div>
                ) : overdue.slice(0, 50).map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-1.5 border-b last:border-b-0 text-xs">
                    <div className="truncate flex-1">
                      <span className="font-medium">{c.name}</span>
                      {!c.phone && <Badge variant="outline" className="ml-2 text-[9px] text-rose-700 border-rose-300">no phone</Badge>}
                    </div>
                    <span className="tabular-nums font-bold text-warning shrink-0">{cur()} {SR(c.credit_balance)}</span>
                  </div>
                ))}
                {overdue.length > 50 && (
                  <div className="text-[10px] text-center text-muted-foreground py-1">…and {overdue.length - 50} more</div>
                )}
              </div>
            </div>
          )}

          {running && current && (
            <div className="space-y-3 py-2" data-testid="bulk-whatsapp-current">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">Now sending to</div>
                <div className="text-lg font-bold mt-0.5">{current.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {current.phone || <span className="text-rose-700 font-semibold">No phone — Skip below</span>}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Owes</span>
                  <span className="tabular-nums font-bold text-warning">{cur()} {SR(current.credit_balance)}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-md bg-success/10 p-2 text-center">
                  <div className="font-bold tabular-nums">{sentCount}</div>
                  <div className="text-[10px] text-muted-foreground">Sent</div>
                </div>
                <div className="rounded-md bg-muted/40 p-2 text-center">
                  <div className="font-bold tabular-nums">{skippedCount}</div>
                  <div className="text-[10px] text-muted-foreground">Skipped</div>
                </div>
                <div className="rounded-md bg-warning/10 p-2 text-center">
                  <div className="font-bold tabular-nums">{Math.max(0, remaining - 1)}</div>
                  <div className="text-[10px] text-muted-foreground">Remaining</div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed flex items-start gap-2">
                <AlertCircle size={12} className="text-info shrink-0 mt-0.5" />
                <span>If WhatsApp didn't open, click <b>Re-send</b> below. Otherwise attach the just-downloaded PDF in WhatsApp, send it, then come back here and click <b>Sent →</b>.</span>
              </p>
            </div>
          )}

          {!running && sentCount + skippedCount > 0 && (
            <div className="rounded-lg border border-success/30 bg-success/5 p-3 text-xs space-y-1">
              <div className="font-bold text-success flex items-center gap-1.5"><Check size={13} />Done</div>
              <div className="text-muted-foreground">{sentCount} sent · {skippedCount} skipped</div>
            </div>
          )}

          <DialogFooter>
            {!running ? (
              <>
                <Button variant="outline" onClick={close}>Close</Button>
                <Button
                  variant="outline"
                  onClick={downloadZip}
                  disabled={overdue.length === 0 || zipping}
                  data-testid="bulk-whatsapp-zip"
                  title="Download all PDFs as a single ZIP (no WhatsApp)"
                >
                  <Download size={14} className="mr-1.5" />
                  {zipping ? 'Zipping…' : 'Download ZIP'}
                </Button>
                <Button
                  onClick={start}
                  disabled={overdue.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="bulk-whatsapp-start"
                >
                  <MessageCircle size={14} className="mr-1.5" />
                  Start ({overdue.length})
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2 w-full">
                <Button variant="outline" onClick={close} data-testid="bulk-whatsapp-cancel">Cancel</Button>
                <div className="flex-1" />
                <Button variant="ghost" onClick={() => onShare(current)} data-testid="bulk-whatsapp-resend">Re-send</Button>
                <Button variant="outline" onClick={() => advance(false)} data-testid="bulk-whatsapp-skip">Skip</Button>
                <Button onClick={() => advance(true)} className="bg-success hover:bg-success/90" data-testid="bulk-whatsapp-mark-sent">
                  <Check size={13} className="mr-1.5" />Sent <ArrowRight size={13} className="ml-1" />
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BulkWhatsAppButton;
